// src/ProductPage/ProductMedia.tsx
import React from "react";
import {
  Box,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useQuery } from "@apollo/client";

import Interactive360 from "./Interactive360";
import MagnifierImage from "./MagnifierImage";
import { buildImageUrl } from "../config";
import { PRODUCT_MEDIA_QUERY } from "../graphql/products";

type BackendProduct = {
  id: string;
  name: string;
  slug?: string | null;
  altText?: string | null;
  customSchema?: string | null;
  productType: string;
  shortDescription?: string | null;
  price: number;
  galleryImages?: string[] | null;
  rotationImages?: string[] | null;
};

type Mode = "images" | "360";

interface ProductMediaProps {
  product: BackendProduct;
}

type ProductMediaData = {
  productMedia: {
    id: string;
    galleryImages?: string[] | null;
    rotationImages?: string[] | null;
  } | null;
};

const ProductMedia: React.FC<ProductMediaProps> = ({ product }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // If product is coming from preview (BestSellers), these are empty
  const propGallery = product.galleryImages ?? [];
  const propRotation = product.rotationImages ?? [];

  const propHasAny = propGallery.length > 0 || propRotation.length > 0;

  // Only fetch media if prop doesn't already have media
  const { data } = useQuery<ProductMediaData>(PRODUCT_MEDIA_QUERY, {
    variables: { id: product.id },
    skip: propHasAny, // ✅ important: prevents extra requests when already have images
    fetchPolicy: "cache-first",
  });

  const fetchedGallery = data?.productMedia?.galleryImages ?? [];
  const fetchedRotation = data?.productMedia?.rotationImages ?? [];

  // ✅ Use prop media if available, otherwise use fetched media
  const galleryPaths = propGallery.length > 0 ? propGallery : fetchedGallery;
  const rotationPaths =
    propRotation.length > 0 ? propRotation : fetchedRotation;

  const galleryImages = galleryPaths.map(buildImageUrl);
  const rotationImages = rotationPaths.map(buildImageUrl);

  const hasRotation = rotationImages.length > 0;
  const hasImages = galleryImages.length > 0;

  const [mode, setMode] = React.useState<Mode>("images");
  const [mobileIndex, setMobileIndex] = React.useState(0);

  React.useEffect(() => {
    setMobileIndex(0);
    setMode("images");
  }, [product.id]);

  const nextMobile = () => {
    if (galleryImages.length < 2) return;
    setMobileIndex((i) => (i + 1) % galleryImages.length);
  };

  const prevMobile = () => {
    if (galleryImages.length < 2) return;
    setMobileIndex(
      (i) => (i - 1 + galleryImages.length) % galleryImages.length,
    );
  };

  if (!hasImages && !hasRotation) {
    return null;
  }

  /* =====================
     MOBILE VIEW
  ====================== */
  if (isMobile) {
    return (
      <Box>
        <Box
          sx={{
            position: "relative",
            borderRadius: 3,
            overflow: "hidden",
            aspectRatio: "1 / 1",
            backgroundColor: "#fff",
          }}
        >
          <IconButton
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: "rgba(255,255,255,0.8)",
            }}
          >
            <FavoriteBorderIcon />
          </IconButton>

          {mode === "images" && hasImages && (
            <>
              <Box
                component="img"
                loading="lazy"
                decoding="async"
                src={galleryImages[mobileIndex]}
                alt={product.altText || product.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  objectPosition: "center",
                  display: "block",
                }}
              />

              {galleryImages.length > 1 && (
                <>
                  <IconButton
                    onClick={prevMobile}
                    sx={{
                      position: "absolute",
                      left: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.75)",
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={nextMobile}
                    sx={{
                      position: "absolute",
                      right: 6,
                      top: "50%",
                      transform: "translateY(-50%)",
                      bgcolor: "rgba(255,255,255,0.75)",
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </>
              )}
            </>
          )}

          {mode === "360" && hasRotation && (
            <Interactive360
              frames={rotationImages}
              ariaLabel="360° view"
              fit="contain"
            />
          )}
        </Box>

        {hasRotation && (
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, val) => val && setMode(val)}
            sx={{
              mt: 1.5,
              width: "100%",
              borderRadius: 2,
              overflow: "hidden",
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 1,
                textTransform: "none",
                fontWeight: 500,
              },
            }}
          >
            <ToggleButton value="images">Images</ToggleButton>
            <ToggleButton value="360">360°</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
    );
  }

  /* =====================
     DESKTOP VIEW
  ====================== */
  const imagesForGrid =
    galleryImages.length >= 4
      ? galleryImages.slice(0, 4)
      : [...galleryImages, ...galleryImages.slice(0, 4 - galleryImages.length)];

  return (
    <Box>
      {hasRotation && (
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, val) => val && setMode(val)}
          sx={{ mb: 2 }}
          size="small"
        >
          <ToggleButton value="images">Images</ToggleButton>
          <ToggleButton value="360">360°</ToggleButton>
        </ToggleButtonGroup>
      )}

      {mode === "images" && (
        <Grid container spacing={2}>
          {imagesForGrid.map((src, idx) => (
            <Grid key={idx} item xs={6}>
              <MagnifierImage src={src} alt={product.altText || product.name} />
            </Grid>
          ))}
        </Grid>
      )}

      {mode === "360" && hasRotation && (
        <Box
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            aspectRatio: "1 / 1",
            backgroundColor: "#fff",
          }}
        >
          <Interactive360
            frames={rotationImages}
            ariaLabel="360° view"
            fit="contain"
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductMedia;
